// Пошук Ctrl+K (SPEC D37, ТЗ §34): нормалізація тексту, розбір часових виразів («Іваненко наступного тижня» → об'єкт + діапазон
// дат), підказка типу об'єкта («викладача Іваненко»), ранжування збігів (точний код → початок назви → входження).
// Українська морфологія не потрібна: регістр і апострофи нормалізуються, «е» і «є» не змішуються.
import { addDays, isoWeekday } from '../calendar/dates.js';
/** Порядок типів у видачі за однакового рангу збігу. */
export const SEARCH_KIND_VALUES = ['GROUP', 'TEACHER', 'SUBJECT', 'ROOM', 'LOCATION', 'DEPARTMENT', 'FACULTY', 'STUDENT'];
export const SEARCH_KIND_LABELS = {
    GROUP: 'Група',
    TEACHER: 'Викладач',
    SUBJECT: 'Дисципліна',
    ROOM: 'Аудиторія',
    LOCATION: 'Корпус / локація',
    DEPARTMENT: 'Кафедра',
    FACULTY: 'Факультет',
    STUDENT: 'Студент',
};
/** Максимум результатів пошуку. */
export const SEARCH_MAX_LIMIT = 20;
/** Мінімальна довжина тексту для пошуку в БД (коротший — лише точний код). */
export const SEARCH_MIN_TEXT = 2;
const APOSTROPHES = /[’ʼ`´‘']/g;
/** Регістр, апострофи (’ ʼ ` ´ ‘ → '), пробіли; «е/є», «и/і» не змішуються. */
export function normalizeSearchText(s) {
    return s.replace(APOSTROPHES, "'").toLocaleLowerCase('uk-UA').replace(/\s+/g, ' ').trim();
}
// ─────────────────────────────── Часові вирази ───────────────────────────────
const L = "[\\p{L}\\d']"; // символ «слова» (літера, цифра, апостроф)
const B = `(?<!${L})`; // початок слова
const E = `(?!${L})`; // кінець слова
/** Дні тижня у формах запиту (знахідний і називний): 1 = Пн … 7 = Нд. */
const WEEKDAY_FORMS = [
    [1, ['понеділок'], 'у понеділок'],
    [2, ['вівторок'], 'у вівторок'],
    [3, ['середу', 'середа'], 'у середу'],
    [4, ['четвер'], 'у четвер'],
    [5, ["п'ятницю", "п'ятниця", 'пятницю', 'пятниця'], "у п'ятницю"],
    [6, ['суботу', 'субота'], 'у суботу'],
    [7, ['неділю', 'неділя'], 'у неділю'],
];
function pad2(n) {
    return String(n).padStart(2, '0');
}
/** «ДД.ММ[.РРРР]» → ISO; рік без вказання — рік «сьогодні»; неіснуюча дата — null. */
function dateOf(d, m, y, today) {
    const day = Number(d);
    const month = Number(m);
    const year = y ? Number(y) : Number(today.slice(0, 4));
    if (!Number.isInteger(day) || !Number.isInteger(month) || month < 1 || month > 12 || day < 1 || year < 1900 || year > 2999)
        return null;
    const dt = new Date(Date.UTC(year, month - 1, day));
    if (dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day)
        return null;
    return `${year}-${pad2(month)}-${pad2(day)}`;
}
function shortUa(iso, withYear) {
    const [y, m, d] = iso.split('-');
    return withYear ? `${d}.${m}.${y}` : `${d}.${m}`;
}
/** Понеділок тижня дати. */
function mondayOf(date) {
    return addDays(date, 1 - isoWeekday(date));
}
const DATE = '(\\d{1,2})\\.(\\d{1,2})(?:\\.(\\d{4}))?';
/** Знайти перший часовий вираз у нормалізованому рядку (позиції — у цьому рядку). */
function findRange(n, today) {
    const hits = [];
    const push = (re, make) => {
        const m = re.exec(n);
        if (!m)
            return;
        const range = make(m);
        if (range)
            hits.push({ start: m.index, end: m.index + m[0].length, range });
    };
    // «з 01.10 по 05.10», «від 01.10.2026 до 05.10.2026», «01.10–05.10»
    push(new RegExp(`${B}(?:з|із|зі|від)\\s+${DATE}\\s+(?:по|до)\\s+${DATE}${E}`, 'u'), (m) => {
        const from = dateOf(m[1], m[2], m[3], today);
        const to = dateOf(m[4], m[5], m[6] ?? m[3], today);
        if (!from || !to || to < from)
            return null;
        const withYear = !!(m[3] || m[6]);
        return { from, to, label: `з ${shortUa(from, withYear)} по ${shortUa(to, withYear)}` };
    });
    push(new RegExp(`${B}${DATE}\\s*[-–—]\\s*${DATE}${E}`, 'u'), (m) => {
        const from = dateOf(m[1], m[2], m[3], today);
        const to = dateOf(m[4], m[5], m[6] ?? m[3], today);
        if (!from || !to || to < from)
            return null;
        const withYear = !!(m[3] || m[6]);
        return { from, to, label: `з ${shortUa(from, withYear)} по ${shortUa(to, withYear)}` };
    });
    // тиждень: «наступного тижня», «на наступному тижні», «наступний тиждень»
    const weekForms = [
        [0, ['цього тижня', 'поточного тижня', 'цьому тижні', 'поточному тижні', 'цей тиждень', 'поточний тиждень'], 'цього тижня'],
        [1, ['наступного тижня', 'наступному тижні', 'наступний тиждень'], 'наступного тижня'],
        [-1, ['минулого тижня', 'попереднього тижня', 'минулому тижні', 'попередньому тижні', 'минулий тиждень', 'попередній тиждень'], 'минулого тижня'],
    ];
    for (const [shift, forms, label] of weekForms) {
        push(new RegExp(`${B}(?:(?:на|у|в)\\s+)?(?:${forms.join('|')})${E}`, 'u'), () => {
            const from = addDays(mondayOf(today), shift * 7);
            return { from, to: addDays(from, 6), label };
        });
    }
    // день: «сьогодні», «завтра», «післязавтра», «вчора»
    const dayForms = [
        ['сьогодні', 0],
        ['завтра', 1],
        ['післязавтра', 2],
        ['вчора', -1],
        ['учора', -1],
    ];
    for (const [word, shift] of dayForms) {
        push(new RegExp(`${B}(?:на\\s+)?${word}${E}`, 'u'), () => {
            const d = addDays(today, shift);
            return { from: d, to: d, label: word === 'учора' ? 'вчора' : word };
        });
    }
    // «у п'ятницю» — найближчий МАЙБУТНІЙ такий день (spec U15 розд. 8 п. 6): сьогодні п'ятниця → наступна п'ятниця
    // (для сьогоднішнього дня є «сьогодні»)
    for (const [weekday, forms, label] of WEEKDAY_FORMS) {
        push(new RegExp(`${B}(?:(?:у|в|на)\\s+)?(?:${forms.join('|')})${E}`, 'u'), () => {
            const d = addDays(today, ((weekday - isoWeekday(today) + 6) % 7) + 1);
            return { from: d, to: d, label };
        });
    }
    // одна дата: «05.10», «на 05.10.2026»
    push(new RegExp(`${B}(?:(?:на|за)\\s+)?${DATE}${E}`, 'u'), (m) => {
        const d = dateOf(m[1], m[2], m[3], today);
        return d ? { from: d, to: d, label: shortUa(d, true) } : null;
    });
    if (!hits.length)
        return null;
    // найраніший у рядку; за однакового початку — найдовший («з 01.10 по 05.10», а не «01.10»)
    hits.sort((a, b) => a.start - b.start || b.end - a.end);
    return hits[0];
}
// ─────────────────────────────── Службові слова й тип ───────────────────────────────
/** Слова, що вказують тип об'єкта (форми без морфологічного аналізу — перелік поширених). */
const KIND_WORDS = [
    ['TEACHER', ['викладач', 'викладача', 'викладачеві', 'викладачу', 'викладачем', 'викладачка', 'викладачки', 'лектор', 'лектора']],
    ['GROUP', ['група', 'групи', 'групу', 'групі', 'групою', 'гр.']],
    ['ROOM', ['аудиторія', 'аудиторії', 'аудиторію', 'ауд.', 'кабінет', 'кабінету', 'кімната', 'кімнати']],
    ['LOCATION', ['корпус', 'корпусу', 'корпусі', 'локація', 'локації']],
    ['DEPARTMENT', ['кафедра', 'кафедри', 'кафедру', 'кафедрі']],
    ['FACULTY', ['факультет', 'факультету', 'факультеті']],
    ['SUBJECT', ['дисципліна', 'дисципліни', 'дисципліну', 'предмет', 'предмета', 'предмету']],
    ['STUDENT', ['студент', 'студента', 'студентка', 'студентки', 'здобувач', 'здобувача']],
];
/** Службові слова запиту на розклад, які не є частиною назви. */
const FILLER_WORDS = new Set([
    'показати',
    'покажи',
    'показать',
    'знайти',
    'знайди',
    'відкрити',
    'відкрий',
    'всі',
    'усі',
    'весь',
    'увесь',
    'заняття',
    'занять',
    'пари',
    'пар',
    'розклад',
    'розкладу',
    'для',
    'на',
    'у',
    'в',
    'за',
    'мені',
]);
const KIND_BY_WORD = new Map(KIND_WORDS.flatMap(([kind, words]) => words.map((w) => [w, kind])));
/** Прибрати діапазон [start, end) з рядка й зайві пробіли. */
function cut(s, start, end) {
    return `${s.slice(0, start)} ${s.slice(end)}`.replace(/\s+/g, ' ').trim();
}
/**
 * Розбір запиту пошуку: часовий вираз (перший у рядку) → діапазон дат; службові слова («показати всі заняття … на»)
 * і слово-тип («викладача») прибираються з тексту; регістр тексту зберігається.
 *
 * «Іваненко наступного тижня» (сьогодні 27.09.2026) → { text: 'Іваненко', range: { from: '2026-09-28', to: '2026-10-04',
 * label: 'наступного тижня' } }. Тиждень — з понеділка; «у п'ятницю» — найближча майбутня п'ятниця (сьогодні не враховується).
 */
export function parseSearchQuery(q, today) {
    // рядок для пошуку виразів: лише апострофи й регістр (довжина та сама, що й в оригіналу — позиції збігаються)
    const original = (q ?? '').replace(/\s+/g, ' ').trim();
    const lowered = original.replace(APOSTROPHES, "'").toLocaleLowerCase('uk-UA');
    const sameLength = lowered.length === original.length;
    let src = sameLength ? original : lowered;
    let n = lowered;
    const hit = findRange(n, today);
    let range = null;
    if (hit) {
        range = hit.range;
        src = cut(src, hit.start, hit.end);
        n = cut(n, hit.start, hit.end);
    }
    // слова: службові прибираємо скрізь, слово-тип — лише перше (воно ж — підказка типу)
    const srcWords = src.split(' ').filter(Boolean);
    const normWords = n.split(' ').filter(Boolean);
    let kind;
    const textWords = [];
    const phraseWords = [];
    for (let i = 0; i < normWords.length; i++) {
        const w = normWords[i];
        const word = srcWords[i] ?? w;
        if (FILLER_WORDS.has(w))
            continue;
        const k = KIND_BY_WORD.get(w);
        if (k && !kind) {
            kind = k;
            phraseWords.push(word);
            continue;
        }
        textWords.push(word);
        phraseWords.push(word);
    }
    const text = textWords.join(' ');
    const out = { text, range };
    if (kind) {
        out.kind = kind;
        const phrase = phraseWords.join(' ');
        if (phrase !== text)
            out.phrase = phrase;
    }
    return out;
}
/** Ранг збігу запиту з об'єктом; null — не збігається. */
export function searchMatchRank(query, target) {
    const q = normalizeSearchText(query);
    if (!q)
        return null;
    const code = normalizeSearchText(target.code ?? '');
    const label = normalizeSearchText(target.label);
    const extra = (target.extra ?? []).map((x) => normalizeSearchText(x ?? '')).filter(Boolean);
    if (code && code === q)
        return 0;
    if (label === q || extra.includes(q))
        return 1;
    if (code && code.startsWith(q))
        return 2;
    const words = [label, ...extra].flatMap((s) => s.split(/[\s\-–—/(),.]+/)).filter(Boolean);
    if (label.startsWith(q) || extra.some((x) => x.startsWith(q)) || words.some((w) => w.startsWith(q)))
        return 3;
    if (label.includes(q) || code.includes(q) || extra.some((x) => x.includes(q)))
        return 4;
    const tokens = q.split(' ').filter(Boolean);
    const hay = [code, label, ...extra].join(' ');
    if (tokens.length > 1 && tokens.every((t) => hay.includes(t)))
        return 5;
    return null;
}
/** Порядок видачі: ранг, тип-підказка першим, порядок типів, назва (uk). */
export function compareSearchItems(a, b, preferKind) {
    if (a.rank !== b.rank)
        return a.rank - b.rank;
    if (preferKind && (a.kind === preferKind) !== (b.kind === preferKind))
        return a.kind === preferKind ? -1 : 1;
    const ka = SEARCH_KIND_VALUES.indexOf(a.kind);
    const kb = SEARCH_KIND_VALUES.indexOf(b.kind);
    if (ka !== kb)
        return ka - kb;
    return a.label.localeCompare(b.label, 'uk');
}
/** Підпис діапазону для інтерфейсу: «наступного тижня: 28.09–04.10». */
export function searchRangeText(range) {
    const sameYear = range.from.slice(0, 4) === range.to.slice(0, 4);
    const f = shortUa(range.from, !sameYear);
    const t = shortUa(range.to, !sameYear);
    return range.from === range.to ? `${range.label}: ${shortUa(range.from, true)}` : `${range.label}: ${f}–${t}`;
}
//# sourceMappingURL=query.js.map